import { useEffect, useRef, useCallback } from "react";

/**
 * Default timeout: 15 minutes (in milliseconds)
 */
const DEFAULT_TIMEOUT = 15 * 60 * 1000;

/**
 * Hook to detect user inactivity and trigger a callback (e.g., logout).
 * @param {function} onTimeout - Function to call when the timer expires.
 * @param {number} [timeout=DEFAULT_TIMEOUT] - Inactivity duration in ms.
 */
const useInactivityTimer = (onTimeout, timeout = DEFAULT_TIMEOUT) => {
  const timeoutIdRef = useRef(null);
  const onTimeoutRef = useRef(onTimeout);
  const lastActivityRef = useRef(Date.now());

  // Always keep the latest version of the callback
  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  /**
   * Clears any existing timer and starts a new one.
   */
  const resetTimer = useCallback(() => {
    // 1. Clear existing timeout
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }

    // 2. Set new timeout
    timeoutIdRef.current = setTimeout(() => {
      console.warn(`Inactivity: Session expired after ${timeout / 60000} minutes.`);
      if (onTimeoutRef.current) {
        onTimeoutRef.current("inactivity");
      }
    }, timeout);
  }, [timeout]);

  /**
   * Handler to reset the timer. 
   * Includes a throttle (2-second gap) to improve performance during heavy movement.
   */
  const handleActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastActivityRef.current > 2000) {
      lastActivityRef.current = now;
      resetTimer();
    }
  }, [resetTimer]);

  useEffect(() => {
    // List of events that indicate user interaction
    const activityEvents = [
      "mousedown",
      "mousemove",
      "keydown",
      "scroll",
      "touchstart",
      "click",
      "wheel"
    ];

    // Attach listeners to the window object
    activityEvents.forEach((event) => {
      // Use passive: true for scroll/touch/wheel to keep the UI smooth
      const options = ["scroll", "touchstart", "touchmove", "wheel"].includes(event)
        ? { passive: true }
        : undefined;
        
      window.addEventListener(event, handleActivity, options);
    });

    // Initial start of the timer
    resetTimer();

    // Cleanup: Remove listeners and clear timeout when component unmounts
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      console.log("Inactivity timer and listeners cleaned up.");
    };
  }, [resetTimer, handleActivity]);

  // This hook handles side effects and does not need to return a value
};

export default useInactivityTimer;